import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  FaSpinner, FaArrowUp, FaArrowDown, FaChevronLeft,
  FaBirthdayCake, FaFlagCheckered
} from 'react-icons/fa';
import { useCompare } from '../context/CompareContext';
import HistoricalSipCalculator from '../components/HistoricalSipCalculator';

/**
 * Helper component for the "At a Glance" cards
 */
const StatCard = ({ title, value, changePercent, changeAbsolute }) => {
  const isPositive = changePercent ? parseFloat(changePercent) >= 0 : null;
  
  return (
    <div className="bg-white shadow-lg rounded-xl p-6">
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
      {changePercent && (
        <div className={`flex items-center text-sm font-medium mt-2 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
          {isPositive ? <FaArrowUp className="mr-1" /> : <FaArrowDown className="mr-1" />}
          {changeAbsolute} ({changePercent}%) 1-Day
        </div>
      )}
    </div>
  );
};

/**
 * Main Page Component
 */
function FundDetailPage() {
  const { schemeCode } = useParams(); // Reads the 'schemeCode' from the URL
  const { addFundToCompare } = useCompare(); // Gets the compare function
  const [fundData, setFundData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Data Calculation Helpers ---
  
  const getLatestNav = (data) => (data && data.length > 0) ? data[0].nav : 'N/A';

  // Calculates 1-Day Change
  const calculateChange = (data, tradingDaysAgo) => {
    if (!data || data.length === 0 || data.length < tradingDaysAgo + 1) {
      return { percent: 'N/A', absolute: 'N/A' };
    }
    const latestNav = parseFloat(data[0].nav);
    const pastNav = parseFloat(data[tradingDaysAgo].nav);
    
    if (pastNav === 0) return { percent: 'N/A', absolute: 'N/A' };

    const absolute = (latestNav - pastNav);
    const percent = (absolute / pastNav) * 100;

    return {
      percent: percent.toFixed(2),
      absolute: absolute.toFixed(4)
    };
  };

  // Calculates 1-Year Return
  const get1YearReturn = (data) => {
    // 252 is the standard number of trading days in a year
    const change = calculateChange(data, 252); 
    return change.percent;
  };

  // --- NEW HELPER FUNCTIONS ---

  // Gets the oldest date from the NAV list (sorted new-to-old)
  const getInceptionDate = (data) => {
    if (!data || data.length === 0) return 'N/A';
    return data[data.length - 1].date; // The last item is the oldest
  };

  // Calculates age in years from the inception date
  const getFundAge = (inceptionDate) => {
    if (inceptionDate === 'N/A') return 'N/A';
    
    const [day, month, year] = inceptionDate.split('-').map(Number);
    const startDate = new Date(year, month - 1, day);
    const today = new Date();
    
    let age = today.getFullYear() - startDate.getFullYear();
    const m = today.getMonth() - startDate.getMonth();
    
    if (m < 0 || (m === 0 && today.getDate() < startDate.getDate())) {
      age--;
    }
    
    return `${age} ${age > 1 ? 'Years' : 'Year'}`;
  };

  // --- End of new functions ---
  
  // --- Data Fetching ---
  useEffect(() => {
    const fetchFundDetails = async () => {
      setIsLoading(true);
      setError(null);
      setFundData(null);

      try {
        const response = await fetch(`https://api.mfapi.in/mf/${schemeCode}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setFundData(data);
      } catch (e) {
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFundDetails();
  }, [schemeCode]); // Re-run this effect if the schemeCode in the URL changes

  // --- Render Logic ---
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <FaSpinner className="animate-spin text-blue-600 h-12 w-12" />
        <span className="ml-4 text-xl text-gray-700">Loading Fund Details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-12">
        <h2 className="text-2xl font-bold text-red-600">Error</h2>
        <p className="text-gray-700 mt-2">Could not load data for this fund.</p>
        <p className="text-sm text-gray-500 mt-1">Details: {error}</p>
        <Link to="/mutual-funds" className="mt-6 inline-flex items-center py-2 px-4 rounded-lg text-white bg-blue-600 hover:bg-blue-700">
          <FaChevronLeft className="mr-2" />
          Back to Dashboard
        </Link>
      </div>
    );
  }

  if (!fundData) return null; // Should not happen, but good practice

  // Destructure data for easier access
  const { meta, data } = fundData;
  const oneDayChange = calculateChange(data, 1);
  const oneYearReturn = get1YearReturn(data);

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* --- Header --- */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          
          {/* --- Navigation Row --- */}
          <div className="flex justify-between items-center mb-2">
            <Link to="/mutual-funds" className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center">
              <FaChevronLeft className="mr-1 h-3 w-3" />
              Back to Dashboard
            </Link>

            {/* --- "Add to Compare" Button --- */}
            <button
              onClick={() => addFundToCompare({ schemeCode: meta.scheme_code, schemeName: meta.scheme_name })}
              className="py-2 px-4 bg-blue-600 text-white font-medium rounded-lg shadow-md hover:bg-blue-700 transition-all"
            >
              Add to Compare
            </button>
          </div>

          <h1 className="text-4xl font-bold text-gray-900">{meta.scheme_name}</h1>
          <p className="text-xl text-gray-600 mt-1">{meta.fund_house}</p>
        </div>
      </div>

      {/* --- Main Content Area --- */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* --- "At a Glance" Stats --- */}
        <h2 className="text-2xl font-bold text-gray-800 mb-6">At a Glance</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Latest NAV"
            value={`₹ ${getLatestNav(data)}`}
            changePercent={oneDayChange.percent}
            changeAbsolute={oneDayChange.absolute}
          />
          <StatCard
            title="1-Year Return"
            value={oneYearReturn === 'N/A' ? 'N/A' : `${oneYearReturn}%`}
          />
          <div className="bg-white shadow-lg rounded-xl p-6">
            <h3 className="text-sm font-medium text-gray-500">Scheme Category</h3>
            <p className="text-3xl font-bold text-gray-900 mt-1">{meta.scheme_category}</p>
          </div>
          <div className="bg-white shadow-lg rounded-xl p-6">
            <h3 className="text-sm font-medium text-gray-500">Scheme Type</h3>
            <p className="text-3xl font-bold text-gray-900 mt-1">{meta.scheme_type}</p>
          </div>
          
          {/* --- NEW STAT CARDS --- */}
          <div className="bg-white shadow-lg rounded-xl p-6">
            <h3 className="text-sm font-medium text-gray-500">Inception Date</h3>
            <div className="flex items-center mt-1">
              <FaFlagCheckered className="h-6 w-6 text-blue-600 mr-3" />
              <p className="text-3xl font-bold text-gray-900">
                {getInceptionDate(data)}
              </p>
            </div>
          </div>
          
          <div className="bg-white shadow-lg rounded-xl p-6">
            <h3 className="text-sm font-medium text-gray-500">Age</h3>
            <div className="flex items-center mt-1">
              <FaBirthdayCake className="h-6 w-6 text-blue-600 mr-3" />
              <p className="text-3xl font-bold text-gray-900">
                {getFundAge(getInceptionDate(data))}
              </p>
            </div>
          </div>
          {/* --- END OF NEW STAT CARDS --- */}

        </div>

        {/* --- HISTORICAL SIP CALCULATOR (New Position) --- */}
        <HistoricalSipCalculator navData={data} />

        {/* --- Historical Data Table (New Position) --- */}
        <div className="mt-12 bg-white shadow-lg rounded-xl overflow-hidden">
          <h2 className="text-2xl font-bold text-gray-800 p-6">Historical Data (Last 10 Days)</h2>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Asset Value (NAV)</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.slice(0, 10).map((navEntry) => (
                <tr key={navEntry.date}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{navEntry.date}</td>
                  <td className="px-6 py-4 whitespace-nowrawp text-sm text-gray-700">₹ {navEntry.nav}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
      </div>
    </div>
  );
}

export default FundDetailPage;